import { format } from "date-fns";
import { sileo } from "sileo";
import { mensajeDeError } from "../mensajeDeError";
import { es } from "date-fns/locale";
import { Pencil, Trash2 } from "lucide-react";
import { useState } from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import EmptyState from "../../../shared/components/EmptyState";
import LoadingOverlay from "../../../shared/components/LoadingOverlay";
import PageHeader from "../../../shared/components/PageHeader";
import {
  useChangelogEntries,
  useDeleteChangelogEntry,
  usePublishChangelogEntry,
  useUnpublishChangelogEntry,
} from "../hooks/useChangelog";
import type { ChangelogEntry, ChangelogKind, ChangelogStatus } from "../types";
import ChangelogEditModal from "./ChangelogEditModal";

type StatusFilter = ChangelogStatus | "all";
type KindFilter = ChangelogKind | "all";

const STATUS_FILTERS: StatusFilter[] = ["all", "draft", "published"];
const KIND_FILTERS: KindFilter[] = [
  "all",
  "feature",
  "improvement",
  "fix",
  "breaking",
  "security",
];

const STATUS_LABELS: Record<StatusFilter, string> = {
  all: "Todos",
  draft: "Borrador",
  published: "Publicado",
};

const KIND_LABELS: Record<KindFilter, string> = {
  all: "Todos",
  feature: "Funcionalidad",
  improvement: "Mejora",
  fix: "Corrección",
  breaking: "Incompatible",
  security: "Seguridad",
};

const KIND_BADGE_CLASS: Record<ChangelogKind, string> = {
  feature: "bg-purple-500/20 text-purple-300",
  improvement: "bg-blue-500/20 text-blue-300",
  fix: "bg-green-500/20 text-green-300",
  breaking: "bg-red-500/20 text-red-300",
  security: "bg-amber-500/20 text-amber-300",
};

const KindBadge = ({ kind }: { kind: ChangelogKind }) => (
  <Badge className={KIND_BADGE_CLASS[kind]}>{KIND_LABELS[kind]}</Badge>
);

const StatusBadge = ({ status }: { status: ChangelogStatus }) => {
  const config: Record<ChangelogStatus, { className: string; label: string }> =
    {
      draft: {
        className: "bg-yellow-500/20 text-yellow-400",
        label: "Borrador",
      },
      published: {
        className: "bg-green-500/20 text-green-400",
        label: "Publicado",
      },
    };
  const { className, label } = config[status];
  return <Badge className={className}>{label}</Badge>;
};

type TipoAccion = "publish" | "unpublish" | "delete";

type ConfirmAction =
  | { type: "publish"; id: string; version: string }
  | { type: "unpublish"; id: string; version: string }
  | { type: "delete"; id: string; version: string }
  | null;

const ACCION_OK: Record<TipoAccion, string> = {
  publish: "Entrada publicada",
  unpublish: "Entrada despublicada",
  delete: "Entrada eliminada",
};

const ACCION_ERROR: Record<TipoAccion, string> = {
  publish: "No se pudo publicar",
  unpublish: "No se pudo despublicar",
  delete: "No se pudo eliminar",
};


const ChangelogPage = () => {
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [kindFilter, setKindFilter] = useState<KindFilter>("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [editing, setEditing] = useState<ChangelogEntry | null | undefined>(
    undefined,
  );
  const [confirmAction, setConfirmAction] = useState<ConfirmAction>(null);

  const { data: listData, isLoading } = useChangelogEntries({
    page: currentPage,
    limit: 20,
    status: statusFilter,
    kind: kindFilter,
    search: searchTerm,
  });

  const publishMutation = usePublishChangelogEntry();
  const unpublishMutation = useUnpublishChangelogEntry();
  const deleteMutation = useDeleteChangelogEntry();

  const entries = listData?.data ?? [];
  const totalPages = listData?.totalPages ?? 1;
  const hasEntries = entries.length > 0;

  const handleStatusChange = (next: StatusFilter) => {
    setStatusFilter(next);
    setCurrentPage(1);
  };

  const handleKindChange = (next: KindFilter) => {
    setKindFilter(next);
    setCurrentPage(1);
  };

  const handleSearch = (value: string) => {
    setSearchTerm(value);
    setCurrentPage(1);
  };

  const formatDate = (value: string) =>
    format(new Date(value), "dd MMM yyyy HH:mm", { locale: es });

  const handleConfirmAction = async () => {
    if (!confirmAction) return;

    const { type, version } = confirmAction;

    try {
      switch (type) {
        case "publish":
          await publishMutation.mutateAsync(confirmAction.id);
          break;
        case "unpublish":
          await unpublishMutation.mutateAsync(confirmAction.id);
          break;
        case "delete":
          await deleteMutation.mutateAsync(confirmAction.id);
          break;
      }

      sileo.success({
        title: ACCION_OK[type],
        description: `Versión ${version}`,
      });
    } catch (err: unknown) {
      // Antes esto era un `finally` pelado: si publicar o borrar fallaba, el diálogo se cerraba
      // igual y el admin se quedaba creyendo que había funcionado.
      sileo.error({
        title: ACCION_ERROR[type],
        description: mensajeDeError(err),
      });
    } finally {
      setConfirmAction(null);
    }
  };

  const getDialogContent = () => {
    if (!confirmAction) return { title: "", description: "", actionLabel: "" };
    switch (confirmAction.type) {
      case "publish":
        return {
          title: "Publicar entrada",
          description: `¿Publicar la entrada de la versión ${confirmAction.version}? Será visible en el sitio público.`,
          actionLabel: "Publicar",
        };
      case "unpublish":
        return {
          title: "Despublicar entrada",
          description: `¿Despublicar la entrada de la versión ${confirmAction.version}? Volverá al estado borrador.`,
          actionLabel: "Despublicar",
        };
      case "delete":
        return {
          title: "Eliminar entrada",
          description: `¿Eliminar la entrada de la versión ${confirmAction.version}? Esta acción no se puede deshacer.`,
          actionLabel: "Eliminar",
        };
    }
  };

  const dialogContent = getDialogContent();
  const isModalOpen = editing !== undefined;

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Changelog"
        subtitle="Gestión de entradas del registro de cambios"
        actions={
          <Button
            onClick={() => setEditing(null)}
            className="bg-purple-600 text-white hover:bg-purple-700"
          >
            Nueva entrada
          </Button>
        }
      />

      <Card className="border-neutral-800 bg-neutral-900/50">
        <CardContent className="space-y-4 p-4">
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex flex-wrap gap-2">
              {STATUS_FILTERS.map((filter) => (
                <Button
                  key={filter}
                  variant={statusFilter === filter ? "default" : "outline"}
                  size="sm"
                  onClick={() => handleStatusChange(filter)}
                  className={
                    statusFilter === filter
                      ? "bg-purple-600 text-white hover:bg-purple-700"
                      : "border-neutral-700 text-neutral-300 hover:bg-neutral-700"
                  }
                >
                  {STATUS_LABELS[filter]}
                </Button>
              ))}
            </div>

            <div className="w-48">
              <Select
                value={kindFilter}
                onValueChange={(value) => handleKindChange(value as KindFilter)}
              >
                <SelectTrigger className="border-neutral-700 bg-neutral-800/50 text-neutral-200">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="border-neutral-700 bg-neutral-900 text-neutral-200">
                  {KIND_FILTERS.map((filter) => (
                    <SelectItem key={filter} value={filter}>
                      {KIND_LABELS[filter]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="min-w-[12rem] flex-1">
              <Input
                type="text"
                placeholder="Buscar por versión, título o tag..."
                value={searchTerm}
                onChange={(e) => handleSearch(e.target.value)}
                className="border-neutral-700 bg-neutral-800/50 text-neutral-200"
              />
            </div>
          </div>

          {isLoading ? (
            <div className="py-12">
              <LoadingOverlay />
            </div>
          ) : !hasEntries ? (
            <EmptyState
              title="No hay entradas"
              description={
                searchTerm
                  ? "No se encontraron entradas con ese criterio."
                  : "Aún no se publicaron entradas en el changelog."
              }
            />
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow className="border-neutral-800 hover:bg-transparent">
                    <TableHead className="text-neutral-400">Versión</TableHead>
                    <TableHead className="text-neutral-400">Tipo</TableHead>
                    <TableHead className="text-neutral-400">Título</TableHead>
                    <TableHead className="text-neutral-400">Estado</TableHead>
                    <TableHead className="text-neutral-400">
                      Publicado
                    </TableHead>
                    <TableHead className="text-neutral-400">Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {entries.map((entry) => (
                    <TableRow
                      key={entry.id}
                      className="border-neutral-800 hover:bg-neutral-800/50"
                    >
                      <TableCell>
                        <span className="font-semibold text-neutral-200">
                          {entry.version}
                        </span>
                      </TableCell>
                      <TableCell>
                        <KindBadge kind={entry.kind} />
                      </TableCell>
                      <TableCell className="max-w-md">
                        <div className="flex flex-col">
                          <span className="truncate text-neutral-200">
                            {entry.title}
                          </span>
                          {entry.tags.length > 0 && (
                            <span className="truncate text-xs text-neutral-500">
                              {entry.tags.join(", ")}
                            </span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <StatusBadge status={entry.status} />
                      </TableCell>
                      <TableCell className="text-neutral-400">
                        {entry.publishedAt
                          ? formatDate(entry.publishedAt)
                          : "-"}
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-2">
                          {entry.status === "draft" ? (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() =>
                                setConfirmAction({
                                  type: "publish",
                                  id: entry.id,
                                  version: entry.version,
                                })
                              }
                              className="bg-green-500/10 text-green-400 hover:bg-green-500/20"
                            >
                              Publicar
                            </Button>
                          ) : (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() =>
                                setConfirmAction({
                                  type: "unpublish",
                                  id: entry.id,
                                  version: entry.version,
                                })
                              }
                              className="bg-neutral-700 text-neutral-300 hover:bg-neutral-600"
                            >
                              Despublicar
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setEditing(entry)}
                            className="bg-purple-500/10 text-purple-300 hover:bg-purple-500/20"
                            aria-label={`Editar entrada ${entry.version}`}
                            title="Editar"
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() =>
                              setConfirmAction({
                                type: "delete",
                                id: entry.id,
                                version: entry.version,
                              })
                            }
                            className="bg-red-500/10 text-red-400 hover:bg-red-500/20"
                            aria-label={`Eliminar entrada ${entry.version}`}
                            title="Eliminar"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {totalPages > 1 && (
                <div className="flex items-center justify-between border-t border-neutral-800 pt-4">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      setCurrentPage((prev) => Math.max(1, prev - 1))
                    }
                    disabled={currentPage === 1}
                    className="border-neutral-700 text-neutral-300 hover:bg-neutral-700"
                  >
                    Anterior
                  </Button>
                  <span className="text-sm text-neutral-400">
                    Página {currentPage} de {totalPages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      setCurrentPage((prev) => Math.min(totalPages, prev + 1))
                    }
                    disabled={currentPage === totalPages}
                    className="border-neutral-700 text-neutral-300 hover:bg-neutral-700"
                  >
                    Siguiente
                  </Button>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      <AlertDialog
        open={confirmAction !== null}
        onOpenChange={() => setConfirmAction(null)}
      >
        <AlertDialogContent className="border-neutral-800 bg-neutral-900">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-neutral-100">
              {dialogContent.title}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-neutral-400">
              {dialogContent.description}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-neutral-700 bg-transparent text-neutral-300 hover:bg-neutral-800 hover:text-neutral-100">
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmAction}
              className={
                confirmAction?.type === "delete"
                  ? "bg-red-600 text-white hover:bg-red-700"
                  : "bg-purple-600 text-white hover:bg-purple-700"
              }
            >
              {dialogContent.actionLabel}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {isModalOpen && (
        <ChangelogEditModal
          open={isModalOpen}
          entry={editing}
          onClose={() => setEditing(undefined)}
        />
      )}
    </div>
  );
};

export default ChangelogPage;
